import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { environment } from '@lentau/core/environments';
import { APP_BASE_HREF_PROVIDER } from '@lentau/ui-kit/core/tokens/ssr';
import { WINDOW } from '@lentau/ui-kit/core/tokens/window';
import { dataInterceptor } from '@lentau/api/application/interceptors/data.interceptor';
import {
  httpErrorInterceptor,
  httpNoticesInterceptor,
} from '@lentau/api/application/interceptors/http-error.interceptor';
import { jrpcErrorInterceptor } from '@lentau/api/application/interceptors/jrpc-error.interceptor';
import { transferStateInterceptor } from '@lentau/api/application/interceptors/transfer-state.interceptor';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { ENVIRONMENT } from '@lentau/ui-kit/core/tokens/environment';
import {
  checkSearchBot,
  setIsHydrationStartPage,
  setIsNewUserOrBot,
  StoreBootstrapModule,
} from '@lentau/core/application/shared';
import { provideClientHydration, withEventReplay, withNoHttpTransferCache } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { CommonBootstrapTokens } from '../tokens';
import { ErrorHandlerConfig } from '@lentau/core/sentry/application/logger.config';
import { Configuration } from 'angular-api-gateway/v1/configuration';
import { Configuration as ConfigurationV2 } from 'angular-api-gateway/v2/configuration';
import { BASE_PATH } from 'angular-api-gateway/v1/variables';
import { BASE_PATH as BASE_PATH_V2 } from 'angular-api-gateway/v2/variables';
import {
  ApiGatewayBasePath,
  ApiGatewayConfig,
  ApiGatewayBasePathV2,
  ApiGatewayConfigV2,
} from '@lentau/core/api/gateway';
import { cartPageViewInterceptor } from '@lentau/cart/application/interceptors/cart-page-view.interceptor';
import { growthBookInterceptor } from '@lentau/core/growth-book/application';
import { PlatformService } from '@lentau/ui-kit/core/services/platform/platform.service';
import { IS_HYDRATE_START_PAGE } from '@lentau/ui-kit/core/tokens/is-hydrate-start-page';
import { MFE_REACT_BASE_PATH } from '@lentau/landings/constructor/mfe/shared/mfe-base-path';
import { MfeReactBasePath } from '@lentau/landings/constructor/mfe/shared/mfe-base-path';
import { provideLogoutStorageCleanupKeys } from '@lentau/auth/shared/clear-storage-keys/provide-logout-storage-cleanup-keys';
import { VIEWED_PRODUCTS_KEY } from '@lentau/search/viewed-products/shared/viewed-products/viewed-product-key.const';
import { IsAdultPopupHidden } from '@lentau/is-adult-show';

export const multiBrandCommonConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        dataInterceptor,
        cartPageViewInterceptor,
        jrpcErrorInterceptor,
        httpErrorInterceptor,
        httpNoticesInterceptor,
        growthBookInterceptor,
        transferStateInterceptor,
      ])
    ),
    importProvidersFrom(StoreBootstrapModule),
    provideClientHydration(withNoHttpTransferCache(), withEventReplay()),
    provideZonelessChangeDetection(),
    provideLogoutStorageCleanupKeys([VIEWED_PRODUCTS_KEY, IsAdultPopupHidden]),
    provideAppInitializer(() => {
      const initializerFn: () => void = checkSearchBot(
        inject(BrowserApiSupportService),
        inject(WINDOW) as Window & typeof globalThis
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn: () => void = setIsNewUserOrBot();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn: () => void = setIsHydrationStartPage(inject(IS_HYDRATE_START_PAGE), inject(DOCUMENT));
      return initializerFn();
    }),
    APP_BASE_HREF_PROVIDER,
    {
      provide: LOCALE_ID,
      useValue: 'ru',
    },
    ...ErrorHandlerConfig,
    {
      provide: ENVIRONMENT,
      useFactory: () => environment,
    },
    {
      provide: BASE_PATH,
      useFactory: ApiGatewayBasePath,
      deps: [WINDOW, PlatformService],
    },
    {
      provide: BASE_PATH_V2,
      useFactory: ApiGatewayBasePathV2,
      deps: [WINDOW, PlatformService],
    },
    {
      provide: MFE_REACT_BASE_PATH,
      useFactory: MfeReactBasePath,
      deps: [WINDOW],
    },
    {
      provide: Configuration,
      useFactory: ApiGatewayConfig,
      deps: [WINDOW],
    },
    {
      provide: ConfigurationV2,
      useFactory: ApiGatewayConfigV2,
      deps: [WINDOW],
    },
    ...CommonBootstrapTokens,
  ],
};
